import { Capacitor } from "@capacitor/core";
import type {} from "./consent";

// Public Metricool site identifier supplied by Rafael, not an API secret.
export const METRICOOL_HASH = "b43669edcec69b71762129559008c2df";
export const METRICOOL_SCRIPT = "https://tracker.metricool.com/resources/be.js";
const SCRIPT_ID = "vyv-metricool-tracker";
const PUBLIC_PATHS = new Set(["/", "/welcome", "/pricing"]);
const MARKETING_PARAMS = new Set([
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "fbclid", "gclid", "ttclid",
]);

declare global {
  interface Window {
    beTracker?: { t: (options: { hash: string }) => void };
  }
}

let initialized = false;
let lastHref: string | null = null;
let loading: Promise<boolean> | null = null;
let consentAllowed = false;

// The vendor reads the FULL URL/referrer. Never load it on app, auth,
// payment, health or user-content routes; don't pass arbitrary query data.
export function isMetricoolPublicUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.port || url.username || url.password) return false;
    if (!["aivyv.com", "www.aivyv.com"].includes(url.hostname)) return false;
    if (!PUBLIC_PATHS.has(url.pathname) || url.hash) return false;
    return Array.from(url.searchParams).every(([key, val]) =>
      MARKETING_PARAMS.has(key) && val.length <= 256 && /^[a-zA-Z0-9_. +~-]*$/.test(val),
    );
  } catch {
    return false;
  }
}

function analyticsAllowed(): boolean {
  try {
    const state = window.Termly?.getConsentState?.();
    // No SDK / unknown consent / errors => no new third-party tracking.
    return !!state && typeof state === "object" &&
      (state as Record<string, unknown>).analytics === true;
  } catch {
    return false;
  }
}

function contextAllowed(): boolean {
  if (typeof window === "undefined" || Capacitor.isNativePlatform()) return false;
  if (!isMetricoolPublicUrl(window.location.href)) return false;
  // Modern cross-origin referrers are origins. Same-site private paths and
  // referrers carrying queries are deliberately excluded, not forwarded.
  if (document.referrer) {
    try {
      const ref = new URL(document.referrer);
      if (!/^https?:$/.test(ref.protocol) || ref.search || ref.hash || ref.username || ref.password) return false;
      if (["aivyv.com", "www.aivyv.com"].includes(ref.hostname) && !PUBLIC_PATHS.has(ref.pathname)) return false;
    } catch {
      return false;
    }
  }
  return analyticsAllowed();
}

function loadTracker(): Promise<boolean> {
  if (window.beTracker?.t) return Promise.resolve(true);
  if (loading) return loading;
  loading = new Promise<boolean>((resolve) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = METRICOOL_SCRIPT;
    script.async = true;
    script.referrerPolicy = "no-referrer";
    let finished = false;
    const finish = (ok: boolean) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(deadline);
      script.onload = null;
      script.onerror = null;
      if (!ok) {
        script.remove();
        console.warn("[metricool] Tracker unavailable; site remains usable.");
      }
      resolve(ok);
    };
    const deadline = window.setTimeout(() => finish(false), 12000);
    script.onload = () => finish(typeof window.beTracker?.t === "function");
    script.onerror = () => finish(false);
    document.head.appendChild(script);
  });
  return loading;
}

/** At most one pageview per contiguous public URL; React remounts dedupe. */
export async function metricoolPageView(): Promise<void> {
  if (!contextAllowed()) {
    lastHref = null;
    return;
  }
  const href = window.location.href;
  if (href === lastHref) return;
  if (!(await loadTracker())) return;
  // Consent or route may have changed while the script was downloading.
  if (!contextAllowed() || window.location.href !== href || href === lastHref) return;
  try {
    window.beTracker?.t({ hash: METRICOOL_HASH });
    lastHref = href;
  } catch {
    console.warn("[metricool] Pageview failed; site remains usable.");
  }
}

/** One page-lifetime observer, web production only. No changes to other SDKs. */
export function initMetricool(): void {
  if (initialized || typeof window === "undefined" || Capacitor.isNativePlatform()) return;
  if (!["aivyv.com", "www.aivyv.com"].includes(window.location.hostname)) return;
  initialized = true;
  let bound: Window["Termly"];
  const check = () => {
    // Termly can arrive after the React bundle; bind its documented events
    // when available and retain a slow fallback for initialization races.
    if (window.Termly && window.Termly !== bound && window.Termly.on) {
      bound = window.Termly;
      try {
        bound.on?.("initialized", check);
        bound.on?.("consent", check);
      } catch {
        console.warn("[metricool] Consent event hook unavailable; polling instead.");
      }
    }
    const next = analyticsAllowed();
    if (!next) lastHref = null;
    const granted = next && !consentAllowed;
    consentAllowed = next;
    if (granted) void metricoolPageView();
  };
  check();
  window.setInterval(check, 2000);
  window.addEventListener("focus", check);
}
